import { cn } from "@ds/lib/utils";

/**
 * THREE NUMBERS ACROSS, THE WAY EVERY TRAINING APP SHOWS THEM.
 *
 * ═══ WHY THIS EXISTS: THE SCREENS WERE WRITTEN FOR A DEVELOPER ═══
 *
 * They showed a definition list — a label on the left, a value on the right, one row per fact,
 * six rows deep, boxed. That is how you dump a record for inspection, and it gives every number
 * the same weight, so nothing is the answer and everything is data. The screens read like a
 * database row rather than like something an athlete opens between sets.
 *
 * The idiom every training app converged on instead is one horizontal row: the value large, its
 * name tiny and quiet underneath, two or three across, hairlines between. It works because the
 * VALUE is what you are scanning for and the label is only there for the half-second you need it
 * — which is the exact inverse of a definition list.
 *
 * ═══ 🚨 THREE, AND THE THIRD IS THE LIMIT ═══
 *
 * At four the type has to shrink to fit a 375pt phone and every number becomes equally small,
 * which is how a row of stats turns back into a table. If a screen has more than three facts
 * worth showing, the right answer is that some of them are not worth showing HERE — put them
 * behind the thing they explain, or on the screen that is about them.
 *
 * ⚠️ NO CARD, NO BORDER, NO BACKGROUND. The system builds hierarchy from surface and type, and a
 * box around these would put them at the same visual level as the chart above — which is the
 * thing they are supposed to be subordinate to.
 */

export interface Stat {
  /** The number, already formatted and already in the athlete's own units. */
  value: string;
  /** What it counts. Tiny, uppercase, and never longer than two words. */
  label: string;
  /**
   * A caveat that belongs to THIS number and to no other — "62% measured" under a volume.
   *
   * 🔑 IT HANGS OFF THE FIGURE IT QUALIFIES rather than floating near the screen's heading. A
   * caveat a reader has to associate with the right number by proximity is a caveat that gets
   * attached to the wrong one.
   */
  note?: string;
}

export function StatRow({ stats, className }: { stats: readonly Stat[]; className?: string }) {
  const shown = stats.slice(0, 3);
  if (shown.length === 0) return null;

  return (
    <dl data-testid="stat-row" className={cn("flex items-stretch", className)}>
      {shown.map((stat, index) => (
        <div
          key={stat.label}
          className={cn(
            "flex min-w-0 flex-1 flex-col gap-0.5",
            /* A HAIRLINE BETWEEN, NOT AROUND. It separates without boxing, which is what keeps
               the row subordinate to the chart above it. */
            index > 0 && "border-border border-l pl-3",
            index < shown.length - 1 && "pr-3",
          )}
        >
          <dd className="text-title truncate tabular-nums">{stat.value}</dd>
          <dt className="text-eyebrow text-muted-foreground truncate uppercase">{stat.label}</dt>
          {stat.note === undefined ? null : (
            <dd className="text-eyebrow text-foreground-faint truncate">{stat.note}</dd>
          )}
        </div>
      ))}
    </dl>
  );
}
